'use client'
import { DatumTags, DatumType } from '@crystal-eyes/types'
import SectionCard from '../components/SectionCard'
import { Icons } from '@crystal-eyes/components/elements/Icon/Icon'
import styles from './TeamRoles.module.scss'
import usePersonality from '@crystal-eyes/hooks/profiles/usePersonality'
import usePersonalInfo from '@crystal-eyes/hooks/profiles/usePersonalInfo'
import { useState } from 'react'
import useDatums from '@crystal-eyes/hooks/profiles/useDatums'
import LoadingBar from '@crystal-eyes/components/elements/Loader/LoadingBar'
import useAuth from '@crystal-eyes/hooks/useAuth'
import classNamesBind from 'classnames/bind'
const classNames = classNamesBind.bind(styles)

interface Identity {
  id: string
  personalInfo?: { firstName?: string }
}

export default function TeamRoles({ identity }: { identity: Identity }) {
  const { data: personalInfo } = usePersonalInfo({ identity })
  const { data: auth } = useAuth()
  const {
    data: personality,
    loading: personalityLoading,
    error: personalityError,
  } = usePersonality({ identity })

  const [selectedOption, setSelectedOption] =
    useState<keyof typeof OPTIONS_TO_TAGS>('As a leader')

  if (!auth?.authed) return null
  if (!personalityLoading && !personalityError && !personality?.discType)
    return null

  return (
    <SectionCard
      cardId="team_roles"
      icon={Icons.Team}
      title="Team Roles"
      sidebarTitle="Team roles"
      className={styles.teamRoles}
    >
      <div className="role-selection">
        <div className={classNames('label')}>
          How {personalInfo?.fullName} works
        </div>
        <select
          className="CE select-menu"
          onChange={evt => setSelectedOption(evt.target.value)}
          aria-label="Team Roles Category"
        >
          {Object.keys(OPTIONS_TO_TAGS).map(opt => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>

      <div className="situations">
        {OPTIONS_TO_TAGS[selectedOption].map(situation => (
          <Situation
            identity={identity}
            personalInfo={personalInfo}
            situation={situation}
            key={situation.action}
          />
        ))}
      </div>
    </SectionCard>
  )
}

function Situation({
  personalInfo,
  identity,
  situation,
}: {
  personalInfo?: Identity['personalInfo']
  identity?: Identity
  situation: SituationDesc
}) {
  const { data: personality } = usePersonality({ identity })
  const { data: datums } = useDatums({
    personality,
    tags: situation.tags,
    type: situation.type,
    limit: 5,
  })

  const isPaywalled = datums?.find(datum => datum.premiumBlur)

  if (!personality?.archetype || !datums)
    return (
      <div className="situation loading">
        <LoadingBar width={'80%'} />
      </div>
    )
  return (
    <div className="situation">
      <h4 className={`CE disc-color ${personality.discType}`}>
        An {personality.archetype.title} ({personality.discType}) like{' '}
        {personalInfo?.firstName} is likely to {situation.action}...
      </h4>

      <div className={`datums ${isPaywalled ? 'paywalled' : ''}`}>
        <ul>
          {datums.map(datum => (
            <li key={datum.id}>{datum.text}</li>
          ))}
        </ul>
      </div>
    </div>
  )
}

type SituationDesc = { action: string; tags: DatumTags[]; type: DatumType }

const OPTIONS_TO_TAGS: {
  [key: string]: SituationDesc[]
} = {
  'As a leader': [
    {
      action: 'thrive leading a team that',
      tags: [DatumTags.WorkingWithSubordinate],
      type: DatumType.Energizer,
    },
    {
      action: 'struggle leading a team that',
      tags: [DatumTags.WorkingWithSubordinate],
      type: DatumType.Drainer,
    },
  ],
  'With peers': [
    {
      action: 'thrive with peers who',
      tags: [DatumTags.WorkingWithPeer],
      type: DatumType.Energizer,
    },
    {
      action: 'struggle with peers who',
      tags: [DatumTags.WorkingWithPeer],
      type: DatumType.Drainer,
    },
  ],
  'Under a leader': [
    {
      action: 'thrive under a boss who',
      tags: [DatumTags.WorkingWithManager],
      type: DatumType.Energizer,
    },
    {
      action: 'struggle under a boss who',
      tags: [DatumTags.WorkingWithManager],
      type: DatumType.Drainer,
    },
  ],
  'With ideal responsibilities': [
    {
      action: 'thrive in a role when',
      tags: [],
      type: DatumType.Energizer,
    },
    {
      action: 'struggle in a role when',
      tags: [],
      type: DatumType.Drainer,
    },
  ],
}
